import {
  Anchor,
  Badge,
  Breadcrumbs,
  Button,
  Card,
  Container,
  Divider,
  Grid,
  Group,
  Stack,
  Table,
  Text,
  Title,
} from '@mantine/core'
import { useDisclosure } from '@mantine/hooks'
import { modals } from '@mantine/modals'
import { notifications } from '@mantine/notifications'
import { createFileRoute, Link } from '@tanstack/react-router'
import dayjs from 'dayjs'
import { useTranslation } from 'react-i18next'
import { LuSparkles } from 'react-icons/lu'
import { AiDraftInvoiceModal } from '@/frontend/components/marina/AiDraftInvoiceModal'
import { CurrencyText } from '@/frontend/components/marina/CurrencyText'
import { MoneyText } from '@/frontend/components/marina/MoneyText'
import { StatusBadge } from '@/frontend/components/marina/StatusBadge'
import {
  useActivateContract,
  useContract,
  useMarkPrepared,
  useSubmitReview,
  useTerminateContract,
} from '@/frontend/hooks/useContracts'

export const Route = createFileRoute('/dashboard/accounts/contracts/$id')({
  component: ContractDetail,
})

function ContractDetail() {
  const { id } = Route.useParams()
  const { t } = useTranslation()
  const { data, isLoading } = useContract(id)
  const markPrepared = useMarkPrepared()
  const submitReview = useSubmitReview()
  const activate = useActivateContract()
  const terminate = useTerminateContract()
  const [aiModalOpened, aiModalH] = useDisclosure(false)

  if (isLoading)
    return (
      <Container size="xl" py="md">
        Loading…
      </Container>
    )
  if (!data)
    return (
      <Container size="xl" py="md">
        Not found
      </Container>
    )
  const c = data.contract
  const customerName = c.customer ? `${c.customer.firstName} ${c.customer.lastName}`.trim() : '—'

  const handleAction = async (mutate: typeof markPrepared, nextLabel: string) => {
    try {
      await mutate.mutateAsync(c.id)
      notifications.show({
        title: 'Contract updated',
        message: `Status changed to ${nextLabel}.`,
        color: 'teal',
      })
    } catch (e: unknown) {
      notifications.show({
        title: 'Failed',
        message: e instanceof Error ? e.message : 'Please try again',
        color: 'red',
      })
    }
  }

  const confirmAction = (
    title: string,
    msg: string,
    mutate: typeof markPrepared,
    nextLabel: string,
    color: 'teal' | 'red' = 'teal',
  ) =>
    modals.openConfirmModal({
      title,
      children: <Text size="sm">{msg}</Text>,
      labels: { confirm: title, cancel: 'Cancel' },
      confirmProps: { color },
      onConfirm: () => handleAction(mutate, nextLabel),
    })

  return (
    <Container size="xl" py="md">
      <Stack gap="lg">
        <Breadcrumbs>
          <Anchor component={Link} to="/dashboard/accounts/contracts" c="dimmed">
            Contracts
          </Anchor>
          <span>{c.code}</span>
        </Breadcrumbs>

        <Card padding="lg" radius="md">
          <Group justify="space-between" align="flex-start" wrap="nowrap">
            <div>
              <Group gap="xs" mb={4}>
                <Title order={3}>{c.code}</Title>
                <StatusBadge status={c.status} kind="contract" />
                <Badge variant="light" color="gray">
                  {c.type}
                </Badge>
              </Group>
              <Text c="dimmed" size="sm">
                {customerName}
                {c.boat?.name && <> · {c.boat.name}</>}
                {c.berth?.code && <> · Berth {c.berth.code}</>}
              </Text>
              <Text c="dimmed" size="sm">
                {dayjs(c.startDate).format('DD MMM YYYY')} → {dayjs(c.endDate).format('DD MMM YYYY')}
              </Text>
            </div>

            <Group gap="xs">
              {c.status === 'DRAFT' && (
                <Button
                  color="laut"
                  loading={markPrepared.isPending}
                  onClick={() =>
                    confirmAction(
                      'Mark as Prepared',
                      'This will lock the contract for further edits.',
                      markPrepared,
                      'PREPARED',
                    )
                  }
                >
                  Mark as Prepared
                </Button>
              )}
              {c.status === 'PREPARED' && (
                <Button
                  color="laut"
                  loading={submitReview.isPending}
                  onClick={() =>
                    confirmAction(
                      'Submit for Review',
                      'Notify reviewers that this contract is ready.',
                      submitReview,
                      'PENDING_REVIEW',
                    )
                  }
                >
                  Submit for Review
                </Button>
              )}
              {c.status === 'PENDING_REVIEW' && (
                <Button
                  color="laut"
                  loading={activate.isPending}
                  onClick={() =>
                    confirmAction(
                      'Activate Contract',
                      'This will activate the contract and start the rental period.',
                      activate,
                      'ACTIVE',
                    )
                  }
                >
                  Activate
                </Button>
              )}
              {c.status === 'ACTIVE' && (
                <>
                  <Button variant="light" color="laut" leftSection={<LuSparkles size={14} />} onClick={aiModalH.open}>
                    {t('page.accounts.contracts.ai_draft_button')}
                  </Button>
                  <Button
                    color="red"
                    loading={terminate.isPending}
                    onClick={() =>
                      confirmAction(
                        'Terminate Contract',
                        'This will end the contract immediately. This action cannot be undone.',
                        terminate,
                        'TERMINATED',
                        'red',
                      )
                    }
                  >
                    Terminate
                  </Button>
                </>
              )}
            </Group>
          </Group>
        </Card>

        <Grid>
          <Grid.Col span={{ base: 12, md: 8 }}>
            <Card padding="lg" radius="md">
              <Text fw={600} mb="sm">
                Items
              </Text>
              <Table>
                <Table.Thead>
                  <Table.Tr>
                    <Table.Th>Description</Table.Th>
                    <Table.Th ta="right">Qty</Table.Th>
                    <Table.Th ta="right">Unit Price</Table.Th>
                    <Table.Th ta="right">Amount</Table.Th>
                  </Table.Tr>
                </Table.Thead>
                <Table.Tbody>
                  {c.items?.map((it) => (
                    <Table.Tr key={it.id}>
                      <Table.Td>{it.description}</Table.Td>
                      <Table.Td ta="right">{Number(it.quantity)}</Table.Td>
                      <Table.Td ta="right">
                        <CurrencyText idr={it.unitPriceIdr} />
                      </Table.Td>
                      <Table.Td ta="right">
                        <CurrencyText idr={it.amountIdr} fw={500} />
                      </Table.Td>
                    </Table.Tr>
                  ))}
                </Table.Tbody>
              </Table>
              <Divider my="md" />
              <Group justify="flex-end" gap="md">
                <Text fw={700}>Total Value:</Text>
                <MoneyText amount={c.totalValueIdr} currency={c.currency} fw={700} size="lg" c="laut.7" />
              </Group>
            </Card>
          </Grid.Col>

          <Grid.Col span={{ base: 12, md: 4 }}>
            <Stack>
              <Card padding="lg" radius="md">
                <Text fw={600} mb="sm">
                  Financials
                </Text>
                <Stack gap={4}>
                  <Field label="Total Value" value={<MoneyText amount={c.totalValueIdr} currency={c.currency} />} />
                  <Field label="Deposit" value={c.depositIdr ? <CurrencyText idr={c.depositIdr} /> : '—'} />
                </Stack>
              </Card>

              {(c.documentUrl || (c.questions && c.questions.length > 0) || c.notes) && (
                <Card padding="lg" radius="md">
                  <Text fw={600} mb="sm">
                    Documents & Notes
                  </Text>
                  {c.documentUrl && (
                    <Anchor href={c.documentUrl} target="_blank" size="sm" c="laut">
                      📄 View signed document →
                    </Anchor>
                  )}
                  {c.notes && (
                    <>
                      <Text size="xs" c="dimmed" tt="uppercase" fw={500} mt="sm">
                        Notes
                      </Text>
                      <Text size="sm">{c.notes}</Text>
                    </>
                  )}
                  {c.questions && c.questions.length > 0 && (
                    <>
                      <Text size="xs" c="dimmed" tt="uppercase" fw={500} mt="sm">
                        Questions
                      </Text>
                      <Stack gap="xs">
                        {c.questions.map((qa, i) => (
                          // biome-ignore lint/suspicious/noArrayIndexKey: read-only Q/A list; index is identity
                          <div key={i}>
                            <Text size="sm" fw={500}>
                              {qa.q}
                            </Text>
                            {qa.a && (
                              <Text size="sm" c="dimmed">
                                {qa.a}
                              </Text>
                            )}
                          </div>
                        ))}
                      </Stack>
                    </>
                  )}
                </Card>
              )}
            </Stack>
          </Grid.Col>
        </Grid>
      </Stack>

      <AiDraftInvoiceModal contractId={c.id} opened={aiModalOpened} onClose={aiModalH.close} />
    </Container>
  )
}

function Field({ label, value }: { label: string; value: React.ReactNode }) {
  return (
    <Group justify="space-between">
      <Text size="sm" c="dimmed">
        {label}
      </Text>
      <Text size="sm">{value}</Text>
    </Group>
  )
}
